import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoading } from '../../../../components/LoadingProvider';
import api from '../../../../api/axios';
import { getIndStates, calculateAge, getCountryCodes } from '../../../../utils/common';
import useAuth from '../../../../hooks/useAuth';
import { showSuccess, showError } from '../../../../utils/toast';
import { useDebouncedCallback } from 'use-debounce';
import { getPublicImgUrl } from '../../../../utils/common';
import AsyncSelect from 'react-select/async';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import moment from 'moment';

export default function BookAppointment() {
    const { setIsLoading } = useLoading();
    const indStates = getIndStates();
    const countryCodes = getCountryCodes();
    const MySwal = withReactContent(Swal);
    const reportFileInputRef = useRef(null);
    const errorDivRef = useRef(null);
    const navigate = useNavigate();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ doctorSearchText, setDoctorSearchText ] = useState('');
    const [ nextBtnDisabled, setNextBtnDisabled ] = useState(false);
    const [ showPatientSearchBox, setShowPatientSearchBox ] = useState(true);
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['clinic'] });

    const [ doctorList, setDoctorList ] = useState([]);
    const [ selectedDoctor, setSelectedDoctor ] = useState(null);
    const [ selectedSlot, setSelectedSlot ] = useState(null);
    const [ currentStep, setCurrentStep ] = useState(1);
    const [ appointmentId, setAppointmentId ] = useState('');
    const [ appointmentData, setAppointmentData ] = useState({});
    const [ appointmentPaymentId, setAppointmentPaymentId ] = useState('');
    const [ patientList, setPatientList ] = useState([]);
    const [ paymentInitData, setPaymentInitData ] = useState(null);
    const [ paymentMethod, setPaymentMethod ] = useState('phi');
    const prefixOfName = [ 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.' ];
    const [ medicalQuestions, setMedicalQuestions ] = useState({
        selected_patient: {},
        visit_reason: '',
        name_prefix: '',
        name: user?.name || '',
        dob: '',
        age: '',
        gender: '',
        phone_code: '',
        phone_number: '',
        email: '',
        address_line1: '',
        address_line2: '',
        district: '',
        state: '',
        pincode: '',
        country: 'India',
        allergies: '',
        regular_medicines: '',
        weight: '',
        height: '',
        blood_pressure: '',
        pulse_rate: '',
        oxygen_saturation: '',
        blood_sugar: '',
        previous_reports_uploaded: []
    });
    const [ medicalQuestionError, setMedicalQuestionError ] = useState({
        visit_reason: '',
        name_prefix: '',
        name: '',
        dob: '',
        gender: '',
        phone_code: '',
        phone_number: '',
        email: '',
        address_line1: '',
        district: '',
        state: '',
        pincode: '',
        allergies: '',
        regular_medicines: '',
        weight: '',
        height: '',
        blood_pressure: '',
        pulse_rate: '',
        oxygen_saturation: '',
        blood_sugar: '',
        previous_reports_uploaded: ''
    });
    const loadPatientListOptions = (inputValue, callback) => {
        filterPatientListOptions(inputValue, callback);
    };
    const fetchPatientById = async (pid) => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/find-my-patients?pid=${pid}`);
            if (response?.data?.result === 'success') {
                setMedicalQuestions(prev => ({
                    ...prev,
                    selected_patient: response?.data?.data?.patients[0]
                }));
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
            setPatientList([]);
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
            setPatientList([]);
        } finally {
            setIsLoading(false);
        }
    }
    const filterPatientListOptions = useDebouncedCallback(async (inputValue, callback) => {
        if (!inputValue) {
            setPatientList([]);
        }
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/find-my-patients?search=${inputValue}`);
            if (response?.data?.result === 'success') {
                setPatientList(response?.data?.data?.patients?.map(item => ({
                    ...item,
                    value: item?.id,
                    label: item?.label_name
                })));
                callback(response?.data?.data?.patients?.map(item => ({
                    ...item,
                    value: item?.id,
                    label: item?.label_name
                })));
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
            setPatientList([]);
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
            setPatientList([]);
        } finally {
            setIsLoading(false);
        }
    }, 1000);

    // const today = new Date();
    const date = new Date();
    const offset = date.getTimezoneOffset() == 0 ? 0 : -1 * date.getTimezoneOffset();
    let normalized = new Date(date.getTime() + (offset) * 60000);
    let today = new Date(normalized.toLocaleString("en-US", {timeZone: "Asia/Calcutta"}));
    const [selectedDate, setSelectedDate] = useState(today.toISOString().substring(0, 10));
    const [selectedDoctorDate, setSelectedDoctorDate] = useState({});
    const [previousSelectedDoctor, setPreviousSelectedDoctor] = useState(null);
    const [slotConfirmationChecked, setSlotConfirmationChecked] = useState(false);
    const [selectedSlotAvailable, setSelectedSlotAvailable] = useState(null);
    const [refreshedAvailableSlots, setRefreshedAvailableSlots] = useState({});
    const [showOtherSlots, setShowOtherSlots] = useState(false);

    const debounced = useDebouncedCallback(async (value) => {
        setNextBtnDisabled(true);
        setIsLoading(true);
        setDoctorList([]);
        setDoctorSearchText(value);
        try {
            const now = new Date();
            let date = selectedDate+' '+now.getHours()+':'+now.getMinutes()+':'+now.getSeconds();
            const response = await api.get('/doctor/search?text='+value+'&date='+date);
            if (response?.data?.result === 'success') {
                response?.data?.data?.doctors.forEach((doctor, index, arr) => {
                    if(Object.keys(doctor.available_slots).length > 0){
                        for (let key of Object.keys(doctor.available_slots)) {
                            setSelectedDoctorDate({
                                ...selectedDoctorDate,
                                [doctor.id]: key
                            });
                            break;
                        }
                    }
                });
                setDoctorList(response?.data?.data?.doctors || []);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError(response?.data?.message || 'An error occurred');
            }
        } catch (error) {
            showError(error.message);
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
            setSelectedSlot(null);
            setSelectedSlotAvailable(null);
            setSlotConfirmationChecked(false);
            setRefreshedAvailableSlots({});
            setShowOtherSlots(false);
        }
    }, 1000);

    const fetchDoctorById = async (id) => {
        setNextBtnDisabled(true);
        setIsLoading(true);
        setDoctorList([]);
        setDoctorSearchText('');
        try {
            const now = new Date();
            let date = selectedDate+' '+now.getHours()+':'+now.getMinutes()+':'+now.getSeconds();
            const response = await api.get('/doctor/search?id='+id+'&date='+date);
            if (response?.data?.result === 'success') {
                response?.data?.data?.doctors.forEach((doctor, index, arr) => {
                    if(Object.keys(doctor.available_slots).length > 0){
                        for (let key of Object.keys(doctor.available_slots)) {
                            setSelectedDoctorDate({
                                ...selectedDoctorDate,
                                [doctor.id]: key
                            });
                            break;
                        }
                    }
                });
                setDoctorList(response?.data?.data?.doctors || []);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError(response?.data?.message || 'An error occurred');
            }
        } catch (error) {
            showError(error.message);
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
            setSelectedSlot(null);
            setSelectedSlotAvailable(null);
            setSlotConfirmationChecked(false);
            setRefreshedAvailableSlots({});
            setShowOtherSlots(false);
        }
    }

    const fetchAppointmentDetails = async (appointmentId) => {
        setNextBtnDisabled(true);
        try {
            const response = await api.get('/'+userRole+'/appointment/'+appointmentId);
            if (response?.data?.result === 'success') {
                setShowPatientSearchBox(false);
                setAppointmentId(response?.data?.data?.appointment?.id);
                setAppointmentData(response?.data?.data?.appointment || {});
                setMedicalQuestions({
                    selected_patient: response?.data?.data?.patient,
                    visit_reason: response?.data?.data?.appointment?.visit_reason || '',
                    name_prefix: response?.data?.data?.appointment?.patient_name_prefix || '',
                    name: response?.data?.data?.appointment?.patient_name || '',
                    dob: response?.data?.data?.appointment?.patient_dob || '',
                    age: response?.data?.data?.appointment?.patient_age || '',
                    gender: response?.data?.data?.appointment?.patient_gender || '',
                    phone_code: response?.data?.data?.patient?.phone_code || '',
                    phone_number: response?.data?.data?.patient?.phone_number || '',
                    email: response?.data?.data?.appointment?.patient_email || '',
                    address_line1: response?.data?.data?.appointment?.patient_address_line1 || '',
                    address_line2: response?.data?.data?.appointment?.patient_address_line2 || '',
                    district: response?.data?.data?.appointment?.patient_district || '',
                    state: response?.data?.data?.appointment?.patient_state || '',
                    pincode: response?.data?.data?.appointment?.patient_pincode || '',
                    country: response?.data?.data?.appointment?.patient_country || 'India',
                    allergies: response?.data?.data?.appointment?.patient_allergies || '',
                    regular_medicines: response?.data?.data?.appointment?.patient_regular_medicines || '',
                    weight: response?.data?.data?.appointment?.patient_weight || '',
                    height: response?.data?.data?.appointment?.patient_height || '',
                    blood_pressure: response?.data?.data?.appointment?.patient_blood_pressure || '',
                    pulse_rate: response?.data?.data?.appointment?.patient_pulse_rate || '',
                    oxygen_saturation: response?.data?.data?.appointment?.patient_oxygen_saturation || '',
                    blood_sugar: response?.data?.data?.appointment?.patient_blood_sugar || '',
                    previous_reports_uploaded: response?.data?.data?.appointment?.patient_reports || [],
                });
                setSelectedDoctor(response?.data?.data?.doctor);
                setSelectedSlot(response?.data?.data?.doctor?.available_slots || null);
                setPreviousSelectedDoctor(response?.data?.data?.doctor);
                setPaymentMethod(response?.data?.data?.appointment?.payment_method || 'phi');
                setPaymentInitData({
                    appointment_id: response?.data?.data?.appointment?.id,
                    doctor_name: response?.data?.data?.doctor?.name || '',
                    consultation_fee: response?.data?.data?.appointment?.total_amount || response?.data?.data?.doctor?.consultation_fee || '',
                    payment_method: response?.data?.data?.appointment?.payment_method || 'phi',
                    payment_url: response?.data?.data?.appointment?.payment_url || null,
                });
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
                setShowPatientSearchBox(true);
            } else {
                setShowPatientSearchBox(true);
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            setShowPatientSearchBox(true);
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setNextBtnDisabled(false);
        }
    };

    useEffect(() => {
        document.title = 'Varosa - Book Appointments';
        setIsLoading(false);
        if(searchParams.get('t')){
            debounced(searchParams.get('t'));
        }else if(searchParams.get('d')){
            fetchDoctorById(searchParams.get('d'));
        }else if(searchParams.get('p')){
            fetchPatientById(searchParams.get('p'));
        }
        if(searchParams.get('a')){
            fetchAppointmentDetails(searchParams.get('a'));
        }
        if(searchParams.get('err')){
            showError(searchParams.get('err'));
        }
    }, []);
    useEffect(() => {
        setMedicalQuestions(prev => ({
            ...prev,
            visit_reason: '',
            name_prefix: medicalQuestions.selected_patient?.name_prefix || '',
            name: medicalQuestions.selected_patient?.name || '',
            dob: medicalQuestions.selected_patient?.dob || '',
            age: medicalQuestions.selected_patient?.age || '',
            gender: medicalQuestions.selected_patient?.gender || '',
            phone_code: medicalQuestions.selected_patient?.phone_code || '',
            phone_number: medicalQuestions.selected_patient?.phone_number || '',
            email: medicalQuestions.selected_patient?.email || '',
            address_line1: medicalQuestions.selected_patient?.address_line1 || '',
            address_line2: medicalQuestions.selected_patient?.address_line2 || '',
            district: medicalQuestions.selected_patient?.district || '',
            state: medicalQuestions.selected_patient?.state || '',
            pincode: medicalQuestions.selected_patient?.pincode || '',
            country: medicalQuestions.selected_patient?.country || 'India',
            allergies: medicalQuestions.selected_patient?.allergies || '',
            regular_medicines: medicalQuestions.selected_patient?.regular_medicines || '',
            weight: medicalQuestions.selected_patient?.weight || '',
            height: medicalQuestions.selected_patient?.height || '',
            blood_pressure: medicalQuestions.selected_patient?.blood_pressure || '',
            pulse_rate: medicalQuestions.selected_patient?.pulse_rate || '',
            oxygen_saturation: medicalQuestions.selected_patient?.oxygen_saturation || '',
            blood_sugar: medicalQuestions.selected_patient?.blood_sugar || '',
            previous_reports_uploaded: [],
        }));
    }, [medicalQuestions.selected_patient]);
    useEffect(() => {
        switch(appointmentData.booking_status){
            case 'initiated':
            case 'payment failed':
            case 'payment initiated':
                handleNextStep(4);
                break;
            case 'payment paid':
                setCurrentStep(5);
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
                break;
            default:
                handleNextStep(1);
                break;
        }
    }, [previousSelectedDoctor]);

    const validateMedicalQuestionsForm = () => {
        let all_good = true;
        let error = {
            visit_reason: '',
            name_prefix: '',
            name: '',
            dob: '',
            gender: '',
            phone_code: '',
            phone_number: '',
            email: '',
            address_line1: '',
            district: '',
            state: '',
            pincode: '',
            allergies: '',
            regular_medicines: '',
            weight: '',
            height: '',
            blood_pressure: '',
            pulse_rate: '',
            oxygen_saturation: '',
            blood_sugar: '',
            previous_reports_uploaded: ''
        };

        const getValue = (key) => String(medicalQuestions[key] || '').trim();

        if (getValue('name_prefix') === '' || getValue('name_prefix') === 'Choose...') {
            error.name_prefix = 'Please select a valid prefix.';
            all_good = false;
        }
        if (getValue('name') === '' || getValue('name').length < 3) {
            error.name = 'Please enter a valid patient name.';
            all_good = false;
        }
        if (getValue('dob') === '') {
            error.dob = 'Please enter a valid DOB.';
            all_good = false;
        } else {
            const dob = new Date(getValue('dob'));
            const today = new Date();
            const minDob = new Date();
            minDob.setFullYear(today.getFullYear() - 120);

            if (Number.isNaN(dob.getTime())) {
                error.dob = 'Please enter a valid DOB.';
                all_good = false;
            } else if (dob > today) {
                error.dob = 'DOB cannot be in the future.';
                all_good = false;
            } else if (dob < minDob) {
                error.dob = 'The maximum allowed age is 120 years.';
                all_good = false;
            }
        }
        if (!['male', 'female', 'other'].includes(getValue('gender'))) {
            error.gender = 'Please select a valid gender.';
            all_good = false;
        }
        if (getValue('phone_code') === '' || getValue('phone_code') === 'Choose...') {
            error.phone_code = 'Please select a phone code.';
            all_good = false;
        }
        if (getValue('phone_number') === '' || !/^\d{7,15}$/.test(getValue('phone_number'))) {
            error.phone_number = 'Please enter a valid contact number.';
            all_good = false;
        }
        if (getValue('email') !== '' && !/^\S+@\S+\.\S+$/.test(getValue('email'))) {
            error.email = 'Please enter a valid email.';
            all_good = false;
        }
        if (getValue('address_line1') === '') {
            error.address_line1 = 'Please enter a valid address.';
            all_good = false;
        }
        if (getValue('district') === '') {
            error.district = 'Please enter a valid district.';
            all_good = false;
        }
        if (getValue('state') === '') {
            error.state = 'Please select a valid state.';
            all_good = false;
        }
        if (getValue('pincode') === '' || !/^\d{6}$/.test(getValue('pincode'))) {
            error.pincode = 'Please enter a valid pincode (6-digits).';
            all_good = false;
        }

        setMedicalQuestionError(error);
        return all_good;
    };

    const handleMedicalQuestionChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setMedicalQuestions(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setMedicalQuestions(prev => ({
                ...prev,
                [name]: value
            }));
        }
    }

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        for(let file of files){
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!validTypes.includes(file.type)) {
                showError('Please upload a PDF, PNG, JPEG file.');
                reportFileInputRef.current.value = '';
                return;
            }
            if(file.size > 2 * 1024 * 1024){
                showError('File size exceeds 2MB. Please choose a smaller file.');
                reportFileInputRef.current.value = '';
                return;
            }
        }
        setMedicalQuestions(prev => ({
            ...prev,
            previous_reports_uploaded: ((Array.isArray(prev.previous_reports_uploaded) ? [...prev.previous_reports_uploaded, ...files] : [...files]) || []),
        }));
        reportFileInputRef.current.value = '';
    };

    const handleRemoveFile = (index) => {
        const updatedReports = medicalQuestions.previous_reports_uploaded.filter((_, i) => i !== index);
        setMedicalQuestions(prev => ({
            ...prev,
            previous_reports_uploaded: updatedReports,
        }));
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const viewReport = async (report, isItUrl = false) => {
        try{
            if(isItUrl){
                MySwal.fire({
                    title: 'Report',
                    html: `<iframe src="${report}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                    width: '90%',
                    showConfirmButton: true,
                    confirmButtonText: 'Close',
                    customClass: {
                        container: '',
                    },
                });
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result;
                if(base64String){
                    MySwal.fire({
                        title: 'Report',
                        html: `<iframe src="${base64String}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                        width: '90%',
                        showConfirmButton: true,
                        confirmButtonText: 'Close',
                        customClass: {
                            container: '',
                        },
                    });
                }
            };
            reader.readAsDataURL(report);
        }catch(error){
            console.log(error);
            showError('Something went wrong! Please try again later.');
            return;
        }
    };

    const handleMedicalQuestionsSubmit = async (next_step) => {
        try {
            if(!validateMedicalQuestionsForm()){ 
                errorDivRef.current.scrollIntoView({ behavior: 'smooth' });
                return false;
            }
            const now = new Date();
            const postData = new FormData();
            postData.append('appointment_id', appointmentId || '');
            postData.append('doctor_id', selectedDoctor.id || '');
            postData.append('slot_id', selectedSlot.id || '');
            postData.append('book_date', selectedDate+' '+now.getHours()+':'+now.getMinutes()+':'+now.getSeconds());
            postData.append('visit_reason', medicalQuestions.visit_reason || '');
            if(medicalQuestions.selected_patient?.id){
                postData.append('patient_id', medicalQuestions.selected_patient?.id || '');
            }
            postData.append('name_prefix', medicalQuestions.name_prefix || '');
            postData.append('name', medicalQuestions.name || '');
            postData.append('dob', medicalQuestions.dob || '');
            postData.append('age', medicalQuestions.age || '');
            postData.append('gender', medicalQuestions.gender || '');
            postData.append('phone_code', medicalQuestions.phone_code || '');
            postData.append('phone_number', medicalQuestions.phone_number || '');
            postData.append('phone_numbers', `${medicalQuestions.phone_code || ''} ${medicalQuestions.phone_number || ''}`.trim());
            postData.append('address_line1', medicalQuestions.address_line1 || '');
            postData.append('address_line2', medicalQuestions.address_line2 || '');
            postData.append('district', medicalQuestions.district || '');
            postData.append('state', medicalQuestions.state || '');
            postData.append('country', medicalQuestions.country || '');
            postData.append('pincode', medicalQuestions.pincode || '');
            postData.append('email', medicalQuestions.email || '');
            postData.append('allergies', medicalQuestions.allergies || '');
            postData.append('regular_medicines', medicalQuestions.regular_medicines || '');
            postData.append('weight', medicalQuestions.weight || '');
            postData.append('height', medicalQuestions.height || '');
            postData.append('blood_pressure', medicalQuestions.blood_pressure || '');
            postData.append('pulse_rate', medicalQuestions.pulse_rate || '');
            postData.append('oxygen_saturation', medicalQuestions.oxygen_saturation || '');
            postData.append('blood_sugar', medicalQuestions.blood_sugar || '');
            postData.append('mode', 'Website');
            postData.append('device', 'web');
            if (Array.isArray(medicalQuestions.previous_reports_uploaded)) {
                for(let report of medicalQuestions.previous_reports_uploaded){
                    if(report.id){
                        postData.append('existing_previous_reports_ids[]', report.id);
                    }else{
                        postData.append('previous_reports[]', report);
                    }
                }
            }

            setNextBtnDisabled(true);
            setIsLoading(true);
            const response = await api.post('/'+userRole+'/book-appointment', postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.result === 'success') {
                setShowPatientSearchBox(false);
                showSuccess(response.data.message);
                setAppointmentId(response.data.data.appointment_id);
                setMedicalQuestions(prev => ({
                    ...prev,
                    selected_patient: response.data.data.patient
                }));
                setPaymentInitData({
                    appointment_id: response.data.data.appointment_id,
                    doctor_name: selectedDoctor?.name || '',
                    consultation_fee: response.data.data.total_amount || selectedDoctor?.consultation_fee || '',
                    payment_method: paymentMethod,
                });
                if(next_step){
                    setCurrentStep(next_step);
                }
                return true;
            } else if (response?.data?.result === 'failed') {
                setShowPatientSearchBox(true);
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                setShowPatientSearchBox(true);
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            setShowPatientSearchBox(true);
            showError(error.response?.data?.message || 'Error updating profile');
            return false;
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
            return false;
        }
    };

    const getPaymentInitUrl = async (appointment_id) => {
        try{
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', appointment_id);
            const response = await api.post('/'+userRole+'/init-phi-payment', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                setPaymentInitData(response.data.data);
                if (response.data.data.payment_url) {
                    window.location.href = response.data.data.payment_url;
                }
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error while getting initiate payment');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const loadRazorpayCheckoutScript = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }

            const existingScript = document.getElementById('razorpay-checkout-js');
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(true));
                existingScript.addEventListener('error', () => resolve(false));
                return;
            }

            const script = document.createElement('script');
            script.id = 'razorpay-checkout-js';
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const verifyRazorpayPayment = async (gatewayData, razorpayResponse) => {
        try {
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', gatewayData.appointment_id);
            postData.append('razorpay_payment_id', razorpayResponse.razorpay_payment_id);
            postData.append('razorpay_order_id', razorpayResponse.razorpay_order_id);
            postData.append('razorpay_signature', razorpayResponse.razorpay_signature);

            const response = await api.post('/'+userRole+'/verify-razorpay-payment', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setCurrentStep(3);
                setAppointmentData(prev => ({
                    ...prev,
                    payment_status: 'paid',
                    id: response?.data?.data?.appointment_id,
                    paid_amount: response?.data?.data?.paid_amount,
                    appointment_date_time: response?.data?.data?.appointment_date_time,
                }));
                if(response?.data?.data?.redirect_url){
                    window.location.href = response?.data?.data?.redirect_url;
                }
                return true;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Payment verification failed.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error while checking payment status');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
        return false;
    };

    const checkRazorpayPaymentStatus = async (appointment_id) => {
        try {
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', appointment_id);
            const response = await api.post('/'+userRole+'/check-razorpay-payment-status', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                if (response.data.data.payment_status === 'paid') {
                    setCurrentStep(5);
                    setAppointmentData(prev => ({
                        ...prev,
                        payment_status: 'paid'
                    }));
                }
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error while checking payment status');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const initiateRazorpayPayment = async (appointment_id) => {
        try {
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', appointment_id);
            const response = await api.post('/'+userRole+'/init-razorpay-payment', postData);
            if (response?.data?.result !== 'success') {
                showError(response?.data?.message || 'Unable to initiate Razorpay payment');
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item) => showError(item));
                }
                return;
            }

            const gatewayData = response.data.data;
            setPaymentInitData(gatewayData);
            const scriptLoaded = await loadRazorpayCheckoutScript();
            if (!scriptLoaded || !window.Razorpay) {
                showError('Unable to load Razorpay checkout');
                return;
            }

            const razorpayOptions = {
                key: gatewayData.payment_gateway_key,
                amount: Math.round((gatewayData.amount || gatewayData.consultation_fee || 0) * 100),
                currency: gatewayData.currency || 'INR',
                name: 'Varosa',
                description: 'Appointment Consultation Fee',
                order_id: gatewayData.order_id,
                prefill: {
                    name: user?.name || '',
                    email: user?.email || '',
                    contact: `${user?.phone_code || ''}${user?.phone_number || user?.phone || ''}`.trim(),
                },
                theme: {
                    color: '#0d6efd',
                },
                handler: async (razorpayResponse) => {
                    await verifyRazorpayPayment(gatewayData, razorpayResponse);
                },
                modal: {
                    ondismiss: () => {
                        showError('Payment window closed before completion');
                    }
                }
            };

            const razorpay = new window.Razorpay(razorpayOptions);
            razorpay.open();
        } catch (error) {
            showError(error.response?.data?.message || 'Error while initiating Razorpay payment');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const checkPaymentStatus = async (appointment_id) => {
        try{
            setNextBtnDisabled(true);
            setIsLoading(true);
            const postData = new FormData();
            postData.append('appointment_id', appointment_id);
            const response = await api.post('/'+userRole+'/check-phi-payment-status', postData);
            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                if(response.data.data.payment_status === 'paid'){
                    setCurrentStep(4);
                }
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error while getting initiate payment');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
    };

    const getSelectedSlotDate = () => {
        if (!selectedDoctor?.id) {
            return '';
        }
        return selectedDoctorDate[selectedDoctor.id] || '';
    };

    const getSelectedSlotLabel = () => {
        if (!selectedSlot) {
            return '';
        }
        const slotDate = getSelectedSlotDate();
        const dateLabel = slotDate ? moment(slotDate).format('ddd, D MMM YYYY') : '';
        return `${dateLabel}${dateLabel ? ', ' : ''}${selectedSlot.start_datetime}`;
    };

    const refreshDoctorAvailableSlots = async () => {
        if (!selectedDoctor?.id || !selectedSlot?.id) {
            showError('Please select a doctor and time slot');
            return false;
        }
        try {
            setNextBtnDisabled(true);
            setIsLoading(true);
            const response = await api.get(`/doctor/available-slots/${selectedDoctor.id}`);
            if (response?.data?.result === 'success') {
                const slots = response?.data?.data?.slots || {};
                const slotStillAvailable = Object.values(slots)
                    .flat()
                    .some((slot) => slot.id === selectedSlot.id);

                setRefreshedAvailableSlots(slots);
                setSelectedSlotAvailable(slotStillAvailable);
                setSlotConfirmationChecked(true);

                if (!slotStillAvailable) {
                    const firstAvailableDate = Object.keys(slots)[0];
                    if (firstAvailableDate) {
                        setSelectedDoctorDate({
                            ...selectedDoctorDate,
                            [selectedDoctor.id]: firstAvailableDate
                        });
                    }
                    showError('Selected slot is no longer available. Please choose another slot.');
                }
                return slotStillAvailable;
            } else if (response?.data?.result === 'failed') {
                setRefreshedAvailableSlots({});
                setSelectedSlotAvailable(false);
                setSlotConfirmationChecked(true);
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error checking slot availability');
        } finally {
            setNextBtnDisabled(false);
            setIsLoading(false);
        }
        return false;
    };

    const handleToggleOtherSlots = async () => {
        if (showOtherSlots) {
            setShowOtherSlots(false);
            return;
        }

        if (Object.keys(refreshedAvailableSlots).length === 0) {
            await refreshDoctorAvailableSlots();
        }
        setShowOtherSlots(true);
    };

    const handleNextStep = async (step) => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
        if(step === 2){
            if(!previousSelectedDoctor && (!selectedDoctor || !selectedSlot)){
                showError('Please select a doctor and time slot');
                return;
            }
        } else if(step === 3){
            if(!validateMedicalQuestionsForm()){
                errorDivRef.current?.scrollIntoView({ behavior: 'smooth' });
                return;
            }
            if(previousSelectedDoctor){
                setCurrentStep(step);
                return;
            }
            await refreshDoctorAvailableSlots();
            setShowOtherSlots(false);
        } else if(step === 4){
            if(!previousSelectedDoctor){
                const slotAvailable = selectedSlotAvailable === true ? true : await refreshDoctorAvailableSlots();
                if(!slotAvailable){
                    return;
                }
                await handleMedicalQuestionsSubmit(step);
                return;
            }
        } else if(step === 5){
            if (paymentMethod === 'razorpay') {
                await initiateRazorpayPayment(appointmentId);
                return;
            }
            if (paymentInitData?.payment_url) {
                window.location.href = paymentInitData.payment_url;
                return;
            }
            await getPaymentInitUrl(appointmentId);
            return;
        }
        setCurrentStep(step);
    }

    const handleBackStep = (step) => {
        setCurrentStep(step);
    }

    return (
        <>
            <div className="container-fluid py-5 login-container">
                <div className="container">
                    <div className="row g-5">
                        {/* Appointment Booking Form */}
                        <div className="col-12 mb-4">
                            <div className="bg-light rounded p-4">
                                <h2 className="mb-0">
                                    {appointmentId ? 'Appointment Initiated' : 'Book an Appointment'}
                                </h2>
                                {appointmentId && (
                                    <p className="text-success">
                                        Your appointment ID is: <strong>{appointmentId}</strong>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row g-3">
                        <div className="col-12">
                            <ul id="progressbar" className="five-step">
                                <li className={currentStep >= 1 ? 'active' : ''} id="account"><strong>Select Doctor</strong></li>
                                <li className={currentStep >= 2 ? 'active' : ''} id="patient"><strong>Select Patient</strong></li>
                                <li className={currentStep >= 3 ? 'active' : ''} id="slot"><strong>Slot Confirmation</strong></li>
                                <li className={currentStep >= 4 ? 'active' : ''} id="payment"><strong>Payment</strong></li>
                                <li className={currentStep >= 5 ? 'active' : ''} id="confirm"><strong>Finish</strong></li>
                            </ul>
                        </div>
                    </div>
                    <div className={`patient-tab ${currentStep !== 2 ? 'd-none' : '' }`} ref={errorDivRef}>
                        <div className="border-start border-5 border-primary bg-light px-3 py-2 mb-3">
                            <h4 className="text-primary mb-0">Find My Patient</h4>
                        </div>
                        {showPatientSearchBox &&
                        <div className="row g-3 pb-3">
                            <div className="col-md-12">
                                <AsyncSelect 
                                    cacheOptions={patientList} 
                                    defaultOptions={patientList} 
                                    isClearable={true} 
                                    loadOptions={loadPatientListOptions} 
                                    onChange={(selectedOption) => setMedicalQuestions(prev => ({ ...prev, selected_patient: selectedOption})) } 
                                    value={medicalQuestions?.selected_patient}
                                />
                            </div>
                        </div>}
                        {medicalQuestions?.selected_patient?.id && <div className="alert alert-success alert-dismissible fade show" role="alert">
                            Selected Patient: <strong>{medicalQuestions?.selected_patient?.label_name}</strong>, PID: <strong>{medicalQuestions?.selected_patient?.id}</strong>
                            {showPatientSearchBox && 
                            <button type="button" className="btn-close" onClick={()=>{setMedicalQuestions(prev => ({ ...prev, selected_patient: {}}))}}></button>}
                        </div>}
                        <div className="border-start border-5 border-primary bg-light px-3 py-2 mb-3">
                            <h4 className="text-primary mb-0">Patient Form</h4>
                        </div>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label htmlFor="patient_name" className="form-label fw-semibold">Patient Name</label>
                                <div className="input-group mb-3">
                                    <select 
                                        className="form-select py-3 border-primary" 
                                        name="name_prefix" 
                                        value={medicalQuestions.name_prefix} 
                                        onChange={handleMedicalQuestionChange} 
                                        style={{ maxWidth: '120px' }}
                                        disabled={!showPatientSearchBox}
                                    >
                                        <option>Choose...</option>
                                        {prefixOfName.map((prefix) => (
                                            <option key={prefix} value={prefix}>{prefix}</option>
                                        ))}
                                    </select>
                                    <input type="text" className="form-control py-3 border-primary" id="name" name="name" value={medicalQuestions.name} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                </div>
                                {medicalQuestionError.name_prefix && <div className="invalid-feedback d-block">{medicalQuestionError.name_prefix}</div>}
                                {medicalQuestionError.name && <div className="invalid-feedback d-block">{medicalQuestionError.name}</div>}
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-semibold">DOB</label>
                                <input type="date" className="form-control py-3 border-primary" name="dob" value={medicalQuestions.dob} onChange={handleMedicalQuestionChange} max={new Date().toISOString().split('T')[0]} disabled={!showPatientSearchBox} />
                                {medicalQuestionError.dob && <div className="invalid-feedback d-block">{medicalQuestionError.dob}</div>}
                            </div>
                            <div className="col-md-3 d-none">
                                <label htmlFor="age" className="form-label fw-semibold">Age</label>
                                <input type="number" min="0" className="form-control py-3 border-primary" id="age" name="age" value={medicalQuestions.age} onChange={handleMedicalQuestionChange} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-semibold d-block">Gender</label>
                                <select className="form-select py-3 border-primary" name="gender" value={medicalQuestions.gender} onChange={handleMedicalQuestionChange} disabled={!showPatientSearchBox}>
                                    <option value="">Choose...</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                                {medicalQuestionError.gender && <div className="invalid-feedback d-block">{medicalQuestionError.gender}</div>}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Contact Number</label>
                                <div className="input-group mb-3">
                                    <select className="form-select py-3 border-primary" name="phone_code" value={medicalQuestions.phone_code} onChange={handleMedicalQuestionChange} style={{ maxWidth: '120px' }} disabled={!showPatientSearchBox}>
                                        <option>Choose...</option>
                                        {countryCodes.map((country) => (
                                            <option key={country.code} value={country.code}>{country.code}</option>
                                        ))}
                                    </select>
                                    <input type="tel" className="form-control py-3 border-primary" name="phone_number" value={medicalQuestions.phone_number} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                </div>
                                {medicalQuestionError.phone_code && <div className="invalid-feedback d-block">{medicalQuestionError.phone_code}</div>}
                                {medicalQuestionError.phone_number && <div className="invalid-feedback d-block">{medicalQuestionError.phone_number}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="email" className="form-label fw-semibold">Email (Optional)</label>
                                <input type="email" className="form-control py-3 border-primary" id="email" name="email" value={medicalQuestions.email} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.email && <div className="invalid-feedback d-block">{medicalQuestionError.email}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="address_line1" className="form-label fw-semibold">Address Line 1</label>
                                <input type="text" className="form-control py-3 border-primary" id="address_line1" name="address_line1" value={medicalQuestions.address_line1} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.address_line1 && <div className="invalid-feedback d-block">{medicalQuestionError.address_line1}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="address_line2" className="form-label fw-semibold">Address Line 2</label>
                                <input type="text" className="form-control py-3 border-primary" id="address_line2" name="address_line2" value={medicalQuestions.address_line2} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="district" className="form-label fw-semibold">District</label>
                                <input type="text" className="form-control py-3 border-primary" id="district" name="district" value={medicalQuestions.district} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.district && <div className="invalid-feedback d-block">{medicalQuestionError.district}</div>}
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="state" className="form-label fw-semibold">State</label>
                                <select className="form-select py-3 border-primary" name="state" value={medicalQuestions.state} onChange={handleMedicalQuestionChange} disabled={!showPatientSearchBox}>
                                    <option value="">Choose...</option>
                                    {indStates.map((val) => (
                                        <option key={val.key} value={val.key}>{val.name}</option>
                                    ))}
                                </select>
                                {medicalQuestionError.state && <div className="invalid-feedback d-block">{medicalQuestionError.state}</div>}
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="pincode" className="form-label fw-semibold">Pincode</label>
                                <input type="text" className="form-control py-3 border-primary" id="pincode" name="pincode" value={medicalQuestions.pincode} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.pincode && <div className="invalid-feedback d-block">{medicalQuestionError.pincode}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="allergies" className="form-label fw-semibold">Known Allergies (Optional)</label>
                                <textarea className="form-control py-3 border-primary" id="allergies" name="allergies" rows="2" value={medicalQuestions.allergies} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox}></textarea>
                                {medicalQuestionError.allergies && <div className="invalid-feedback d-block">{medicalQuestionError.allergies}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="regular_medicines" className="form-label fw-semibold">Regular Medicines (Optional)</label>
                                <textarea className="form-control py-3 border-primary" id="regular_medicines" name="regular_medicines" rows="2" value={medicalQuestions.regular_medicines} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox}></textarea>
                                {medicalQuestionError.regular_medicines && <div className="invalid-feedback d-block">{medicalQuestionError.regular_medicines}</div>}
                            </div>
                        </div>

                        <div className="border-start border-5 border-primary bg-light px-3 py-2 my-4">
                            <h4 className="text-primary mb-0">Clinical Vitals & Triage</h4>
                        </div>
                        <div className="row g-3">
                            <div className="col-md-4 col-lg-2">
                                <label htmlFor="weight" className="form-label fw-semibold">Weight (kg) (Optional)</label>
                                <input type="text" className="form-control py-3 border-primary" id="weight" name="weight" value={medicalQuestions.weight} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.weight && <div className="invalid-feedback d-block">{medicalQuestionError.weight}</div>}
                            </div>
                            <div className="col-md-4 col-lg-2">
                                <label htmlFor="height" className="form-label fw-semibold">Height (Optional)</label>
                                <input type="text" className="form-control py-3 border-primary" id="height" name="height" value={medicalQuestions.height} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.height && <div className="invalid-feedback d-block">{medicalQuestionError.height}</div>}
                            </div>
                            <div className="col-md-4 col-lg-2">
                                <label htmlFor="blood_pressure" className="form-label fw-semibold">Blood Pressure (Optional)</label>
                                <input type="text" className="form-control py-3 border-primary" id="blood_pressure" name="blood_pressure" value={medicalQuestions.blood_pressure} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.blood_pressure && <div className="invalid-feedback d-block">{medicalQuestionError.blood_pressure}</div>}
                            </div>
                            <div className="col-md-4 col-lg-2">
                                <label htmlFor="pulse_rate" className="form-label fw-semibold">Pulse Rate (bpm) (Optional)</label>
                                <input type="text" className="form-control py-3 border-primary" id="pulse_rate" name="pulse_rate" value={medicalQuestions.pulse_rate} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.pulse_rate && <div className="invalid-feedback d-block">{medicalQuestionError.pulse_rate}</div>}
                            </div>
                            <div className="col-md-4 col-lg-2">
                                <label htmlFor="oxygen_saturation" className="form-label fw-semibold">Oxygen Sat. (SpO2) (Optional)</label>
                                <input type="text" min="0" max="100" className="form-control py-3 border-primary" id="oxygen_saturation" name="oxygen_saturation" value={medicalQuestions.oxygen_saturation} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.oxygen_saturation && <div className="invalid-feedback d-block">{medicalQuestionError.oxygen_saturation}</div>}
                            </div>
                            <div className="col-md-4 col-lg-2">
                                <label htmlFor="blood_sugar" className="form-label fw-semibold">Blood Sugar (Opt) (Optional)</label>
                                <input type="text" min="0" step="0.1" className="form-control py-3 border-primary" id="blood_sugar" name="blood_sugar" value={medicalQuestions.blood_sugar} onChange={handleMedicalQuestionChange} readOnly={!showPatientSearchBox} />
                                {medicalQuestionError.blood_sugar && <div className="invalid-feedback d-block">{medicalQuestionError.blood_sugar}</div>}
                            </div>
                            <div className="col-12">
                                <label className="form-label">Previous Reports and Prescriptions (optional)</label>
                                <input type="file" className="form-control py-3 border-primary" onChange={handleFileUpload} multiple accept=".pdf,.jpg,.jpeg,.png" ref={reportFileInputRef} disabled={!showPatientSearchBox} />
                                {medicalQuestionError.previous_reports_uploaded && <div className="invalid-feedback d-block">{medicalQuestionError.previous_reports_uploaded}</div>}
                                <div className="text-muted mt-2 mb-2">
                                    <small>PDF, JPEG, JPG, or PNG. Max size 2MB per file.</small>
                                </div>
                                {medicalQuestions.previous_reports_uploaded?.length > 0 && (
                                    <div className="mt-2">
                                        <p>Selected files:</p>
                                        <ul className="list-group">
                                            {medicalQuestions.previous_reports_uploaded.map((file, index) => (
                                                file.original_name ? 
                                                (<li key={index} className="list-group-item">
                                                    {file.original_name} ({formatBytes(file.file_size)})
                                                    <span className="float-end">
                                                        <i className="fas fa-eye text-primary me-3" onClick={() => viewReport(file.file_path, true)} style={{cursor: 'pointer'}}></i>
                                                        <i className="fas fa-trash-alt text-danger" onClick={() => handleRemoveFile(index)} style={{cursor: 'pointer'}}></i>
                                                    </span>
                                                </li>)
                                                : (<li key={index} className="list-group-item">
                                                    {file.name} ({formatBytes(file.size)})
                                                    <span className="float-end">
                                                        <i className="fas fa-eye text-primary me-3" onClick={() => viewReport(file, false)} style={{cursor: 'pointer'}}></i>
                                                        <i className="fas fa-trash-alt text-danger" onClick={() => handleRemoveFile(index)} style={{cursor: 'pointer'}}></i>
                                                    </span>
                                                </li>)
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            <div className="col-6 text-left">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={nextBtnDisabled}
                                    onClick={() => handleBackStep(1)}
                                >
                                    Back
                                </button>
                            </div>
                            <div className="col-6 text-end">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={nextBtnDisabled}
                                    onClick={() => handleNextStep(3)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className={`account-tab row g-3 ${currentStep !== 1 ? 'd-none' : '' }`}>
                        {previousSelectedDoctor ? (
                            <div className="col-md-12 wow fadeInUp" data-wow-delay="0.1s">
                                <div className="team-item rounded">
                                    <div className="team-content text-center border border-primary rounded p-4">
                                        <div className="row">
                                            <div className="col-md-5">
                                                <div className="team-img rounded">
                                                    <img src={previousSelectedDoctor.profile_picture || (getPublicImgUrl()+'/profile_8747415.png')} className="img-fluid rounded" alt="" />
                                                </div>
                                            </div>
                                            <div className="col-md-7 text-start">
                                                <h5 className="mb-2">{previousSelectedDoctor.name}</h5>
                                                {previousSelectedDoctor.qualification && <p className="mb-3">Doctor Qualification: {previousSelectedDoctor.qualification}</p>}
                                                <p className="mb-3">Doctor Bio: {previousSelectedDoctor.bio}</p>
                                                <p className="mb-3">Specializations: {previousSelectedDoctor.specializations.map((specialization) => (
                                                    <span key={specialization} className="badge bg-primary me-1">{specialization}</span>
                                                ))}</p>
                                                <p className="mb-3">Consultation Fees: <b>&#8377;{previousSelectedDoctor.consultation_fee}</b></p>
                                            </div>
                                        </div>
                                        <hr />
                                        <p><b>Your choosen appointment date & time: </b></p>
                                        <ul className="nav nav-pills doctor-time-slot">
                                            <li className="nav-item m-1">
                                                <button className="nav-link nav-tab-link active">
                                                    {appointmentData.appointment_date_time}
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                            <div className="col-md-12">
                                <div className="form-group mb-4">
                                    <div className="input-group mb-3" title="Search by Doctor Name / Department">
                                        <input 
                                            type="text" 
                                            className="form-control py-3 border-primary bg-transparent" 
                                            placeholder="Search by Doctor Name / Department" 
                                            onChange={(e) => {
                                                debounced(e.target.value);
                                            }}
                                            defaultValue={searchParams.get('t') || ''}
                                        />
                                        <button type="button" className="btn btn-primary text-white py-1 px-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                                                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {doctorList.map((doctor) => (
                                <div className="col-md-12 wow fadeInUp" data-wow-delay="0.1s" key={doctor.id}>
                                    <div className="team-item rounded">
                                        <div className="team-content text-center border border-primary rounded p-4">
                                            <div className="row">
                                                <div className="col-md-5">
                                                    <div className="team-img rounded">
                                                        <img src={doctor.profile_picture || (getPublicImgUrl()+'/profile_8747415.png')} className="img-fluid rounded" alt="" />
                                                    </div>
                                                </div>
                                                <div className="col-md-7 text-start">
                                                    <h5 className="mb-2">{doctor.name}</h5>
                                                    {doctor.bio && (<p className="mb-3">
                                                        <b>Doctor Bio:</b> {doctor.bio?.length > 100 ? doctor.bio.slice(0, 100) : doctor.bio}
                                                        {doctor.bio?.length > 100 ? (
                                                            <>
                                                            <span className={`d-none rest-bio-${doctor.id}`}>{doctor.bio.slice(100)}</span>
                                                            &nbsp;&nbsp;
                                                            <button onClick={(e) => { 
                                                                e.preventDefault();
                                                                var elements = document.getElementsByClassName(`rest-bio-${doctor.id}`);
                                                                if(elements.length > 0){
                                                                    if(elements[0].classList.contains("d-none")){
                                                                        elements[0].classList.remove("d-none");
                                                                        e.target.textContent = 'Read Less';
                                                                    }else{
                                                                        elements[0].classList.add("d-none");
                                                                        e.target.textContent = 'Read More';
                                                                    }
                                                                }
                                                            }} className="btn btn-outline-success btn-sm ml-3">Read More</button>
                                                            </>
                                                        ) : ''}
                                                    </p>)}
                                                    <p className="mb-3"><b>Specializations:</b> {doctor.specializations.map((specialization) => (
                                                        <span key={specialization} className="badge bg-primary me-1">{specialization}</span>
                                                    ))}</p>
                                                    <p className="mb-3"><b>Consultation Fees: <i>&#8377;{doctor.consultation_fee}</i></b></p>
                                                </div>
                                            </div>
                                            <hr />
                                            {Object.keys(doctor.available_slots).length === 0 && <p className="text-danger">No available slots</p>}
                                            {Object.keys(doctor.available_slots).length > 0 &&
                                                <>
                                                    <p><b>Please choose date to view available time slots</b></p>
                                                    <ul className="nav nav-pills doctor-time-slot">
                                                        {Object.entries(doctor.available_slots).map(([key, value]) => (
                                                            <li key={key} className="nav-item m-1">
                                                                <button className={`nav-link nav-tab-link-${doctor.id}-${key} ${selectedDoctorDate[doctor.id] === key ? 'active' : ''}`} onClick={() => { setSelectedDoctorDate({ ...selectedDoctorDate, [doctor.id]: key }); }}>
                                                                    {moment(key).format('ddd, D MMM YYYY')}
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <hr />
                                                    {Object.entries(doctor.available_slots).map(([key, value]) => (
                                                        <div key={key} className={`nav-body nab-tab-body-${doctor.id}-${key} ${selectedDoctorDate[doctor.id] === key ? 'd-block' : 'd-none'}`}>
                                                            {
                                                                value.map((time) => (
                                                                    <span key={time.id} className="time-list">
                                                                        <input type="radio" className="btn-check" name={`options-outlined-${doctor.id}-${time.id}`} id={`selected-slot-${doctor.id}-${time.id}`} checked={selectedSlot?.id === time?.id} onChange={() => { setSelectedSlot({ ...time }); setSelectedDoctor(doctor); setSelectedSlotAvailable(null); setSlotConfirmationChecked(false); setRefreshedAvailableSlots({}); setShowOtherSlots(false); }} />
                                                                        <label className="btn btn-outline-success m-2" htmlFor={`selected-slot-${doctor.id}-${time.id}`}>{time.start_datetime}</label>
                                                                    </span>
                                                                ))
                                                            }
                                                        </div>)
                                                    )}
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>
                            ))}
                            </>
                        )}
                        <div className="col-6 text-left">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => window.history.go(-1)}
                            >
                                Back
                            </button>
                        </div>
                        <div className="col-6 text-end">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => handleNextStep(2)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                    <div className={`slot-confirmation-tab row g-3 ${currentStep !== 3 ? 'd-none' : '' }`}>
                        <div className="col-12">
                            <div className="border-start border-5 border-primary bg-light px-3 py-2 mb-3">
                                <h4 className="text-primary mb-0">Slot Confirmation</h4>
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="team-content border border-primary rounded p-4">
                                <h5 className="mb-3">{selectedDoctor?.name}</h5>
                                <p className="mb-2"><b>Selected Slot:</b> {getSelectedSlotLabel()}</p>
                                {slotConfirmationChecked && selectedSlotAvailable === true && (
                                    <div className="alert alert-success mb-0" role="alert">
                                        This slot is available. You can continue to payment.
                                    </div>
                                )}
                                {slotConfirmationChecked && selectedSlotAvailable === false && (
                                    <div className="alert alert-warning mb-0" role="alert">
                                        This slot is not available anymore. Please choose another available slot below.
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm mt-4"
                                    disabled={nextBtnDisabled}
                                    onClick={handleToggleOtherSlots}
                                >
                                    {showOtherSlots ? 'Hide Other Slots' : 'Show Other Slots'}
                                </button>
                            </div>
                        </div>
                        {(showOtherSlots || (slotConfirmationChecked && selectedSlotAvailable === false)) && (
                            <div className="col-12">
                                <div className="team-content border border-primary rounded p-4">
                                    <h5 className="mb-3">Available Slots</h5>
                                    {Object.keys(refreshedAvailableSlots).length === 0 && (
                                        <p className="text-danger mb-0">No other slots are currently available for this doctor.</p>
                                    )}
                                    {Object.keys(refreshedAvailableSlots).length > 0 && (
                                        <>
                                            <ul className="nav nav-pills doctor-time-slot">
                                                {Object.entries(refreshedAvailableSlots).map(([key]) => (
                                                    <li key={key} className="nav-item m-1">
                                                        <button
                                                            type="button"
                                                            className={`nav-link nav-tab-link-${selectedDoctor?.id}-${key} ${selectedDoctorDate[selectedDoctor?.id] === key ? 'active' : ''}`}
                                                            onClick={() => setSelectedDoctorDate({ ...selectedDoctorDate, [selectedDoctor.id]: key })}
                                                        >
                                                            {moment(key).format('ddd, D MMM YYYY')}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                            <hr />
                                            {Object.entries(refreshedAvailableSlots).map(([key, value]) => (
                                                <div key={key} className={`nav-body nab-tab-body-${selectedDoctor?.id}-${key} ${selectedDoctorDate[selectedDoctor?.id] === key ? 'd-block' : 'd-none'}`}>
                                                    {value.map((time) => (
                                                        <span key={time.id} className="m-2">
                                                            <input
                                                                type="radio"
                                                                className="btn-check"
                                                                name={`slot-confirmation-${selectedDoctor?.id}-${time.id}`}
                                                                id={`slot-confirmation-${selectedDoctor?.id}-${time.id}`}
                                                                checked={selectedSlot?.id === time?.id}
                                                                onChange={() => {
                                                                    setSelectedSlot({ ...time });
                                                                    setSelectedDoctorDate({ ...selectedDoctorDate, [selectedDoctor.id]: key });
                                                                    setSelectedSlotAvailable(true);
                                                                }}
                                                            />
                                                            <label className="btn btn-outline-success" htmlFor={`slot-confirmation-${selectedDoctor?.id}-${time.id}`}>
                                                                {time.start_datetime}
                                                            </label>
                                                        </span>
                                                    ))}
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="col-6 text-left">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => handleBackStep(2)}
                            >
                                Back
                            </button>
                        </div>
                        <div className="col-6 text-end">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled || selectedSlotAvailable !== true}
                                onClick={() => handleNextStep(4)}
                            >
                                Continue to Payment
                            </button>
                        </div>
                    </div>
                    <div className={`payment-tab feature row g-3 ${currentStep !== 4 ? 'd-none' : '' }`}>
                        <div className="col-12">
                            <div className="row g-4 justify-content-center">
                                <div className="col-md-7 wow fadeInUp" data-wow-delay="0.1s">
                                    <div className="row-cols-1 feature-item p-4">
                                        <div className="col-12">
                                            <div className="feature-icon mb-4">
                                                <div className="p-3 d-inline-flex bg-white rounded">
                                                    <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                                </div>
                                            </div>
                                            <div className="feature-content d-flex flex-column">
                                                <h5 className="mb-4">{paymentInitData?.doctor_name || selectedDoctor?.name || 'Appointment Payment'}</h5>
                                                <p className="mb-3">You have to Pay <b>&#8377;{paymentInitData?.consultation_fee || appointmentData?.total_amount || selectedDoctor?.consultation_fee || '0'}</b></p>
                                                <label className="form-label">Choose payment method</label>
                                                <select
                                                    className="form-select py-3 border-primary"
                                                    value={paymentMethod}
                                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                                >
                                                    <option value="phi">PHI Payment</option>
                                                    <option value="razorpay">Razorpay</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 text-center wow fadeInUp" data-wow-delay="0.2s">
                                    {paymentMethod === 'phi' && (
                                        <>
                                            <button 
                                                className="btn btn-danger rounded-pill text-white py-3 px-5 me-2" 
                                                disabled={nextBtnDisabled} 
                                                onClick={() => checkPaymentStatus(appointmentId)}
                                            >Check Payment Status</button>
                                            <button 
                                                className="btn btn-primary rounded-pill text-white py-3 px-5 me-2" 
                                                disabled={nextBtnDisabled} 
                                                onClick={() => handleNextStep(5)}
                                            >Pay with PHI</button>
                                        </>
                                    )}
                                    {paymentMethod === 'razorpay' && (
                                        <>
                                            <button
                                                className="btn btn-danger rounded-pill text-white py-3 px-5 me-2"
                                                disabled={nextBtnDisabled}
                                                onClick={() => checkRazorpayPaymentStatus(appointmentId)}
                                            >Check Last Payment Status</button>
                                            <button
                                                className="btn btn-primary rounded-pill text-white py-3 px-5"
                                                disabled={nextBtnDisabled}
                                                onClick={() => initiateRazorpayPayment(appointmentId)}
                                            >Pay with Razorpay</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-6 text-left">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => handleBackStep(3)}
                            >Back</button>
                        </div>
                        <div className="col-6 text-end">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => paymentMethod === 'razorpay' ? initiateRazorpayPayment(appointmentId) : handleNextStep(5)}
                            >Next</button>
                        </div>
                    </div>
                    <div className={`confirm-tab feature row g-3 ${currentStep !== 5 ? 'd-none' : '' }`}>
                        <div className="col-12">
                            {appointmentData &&
                            <div className="row g-4 justify-content-center">
                                <div className="col-md-6 col-lg-4 col-xl-3 wow fadeInUp" data-wow-delay="0.1s">
                                    <div className="row-cols-1 feature-item p-4">
                                        <div className="col-12">
                                            <div className="feature-icon mb-4">
                                                <div className="p-3 d-inline-flex bg-white rounded">
                                                    <i className="fas fa-user-md fa-4x text-primary"></i>
                                                </div>
                                            </div>
                                            <div className="feature-content d-flex flex-column">
                                                <h5 className="mb-4">Appointment Confirmed</h5>
                                                <p className="mb-0">You have to paid <b>&#8377;{appointmentData?.paid_amount}</b></p>
                                                <p className="mb-0">Booked Date: <b>{appointmentData?.appointment_date_time}</b></p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 text-center wow fadeInUp" data-wow-delay="0.2s">
                                    <button 
                                        className="btn btn-primary rounded-pill text-white py-3 px-5" 
                                        disabled={nextBtnDisabled} 
                                        onClick={() => { 
                                            navigate(`/dashboard/clinic/appointment/${appointmentData?.id}`);
                                        }}
                                    >Go to Appointment Details</button>
                                </div>
                            </div>}
                        </div>
                        <div className="col-6 text-left">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={true}
                            >Back</button>
                        </div>
                        <div className="col-6 text-end">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={nextBtnDisabled}
                                onClick={() => {
                                    navigate('/dashboard/clinic/appointments');
                                }}
                            >Finish</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
